import * as React from 'react';

import styled from '@emotion/styled';
import { Table, Tbody, Td as TdBase, Tr } from '@lightspeed/cirrus-table';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { IconHelp } from '@lightspeed/flame/Icon/Help';
import { IconLockClosed } from '@lightspeed/flame/Icon/LockClosed';
import { Text } from '@lightspeed/flame/Text';
import { Tooltip } from '@lightspeed/flame/Tooltip';

import { ToolTipTexts } from '../helpers/TooltipTexts';
import { AnalyticsPromoModalLink } from 'root/apps/Analytics/AnalyticsPromoModalLink';
import { usePromoClicked } from 'root/apps/helpers/analyticsPromoUtils';

import { OverlayDiv } from '../ReorderPoints/ReorderPoints.style';

type Props = {
  isLoading: boolean;
  hasAnalytics: boolean;
  showForecast: () => void;
};

const { SuggestedLevelTooltipText, SuggestedNeedTooltipText, SuggestedPointTooltipText } =
  ToolTipTexts;

const GenerateButton = styled(Button)`
  &&& {
    color: #020203;
    font-size: 14px;
    font-weight: bold;
    line-height: 30px;
    width: 200px;
    border: 1px solid #bcc4ca;
    box-sizing: border-box;
    background: linear-gradient(180deg, #ffffff 0%, #f2f5f8 100%);
    backdrop-filter: blur(2px);
    border-radius: 3px;
  }
`;

const Td = styled(TdBase)`
  &&& {
    font-weight: normal;
    color: #000;
    font-size: 13px;
    vertical-align: inherit;
    height: 25px;
    padding: 8px;
    border-left: 1px solid #d7dce1;
    &:first-of-type {
      border-left: none;
    }
  }
`;

const StyledTooltip = styled(Tooltip)`
  width: 300px;
  font-weight: normal;
  z-index: 10;
  font-size: 12px;
  line-height: 18px;
  text-align: left;
`;

export const InitialState: React.FC<Props> = ({ hasAnalytics, showForecast, isLoading }) => {
  const { hasBeenClicked } = usePromoClicked();

  return (
    <Table>
      <Tbody>
        <Tr>
          <Td width="20%" style={{ minWidth: '148px' }}>
            Suggested point{' '}
            <StyledTooltip content={SuggestedPointTooltipText}>
              <IconHelp baseColor="none" detailsColor1="#232429" detailsColor2="#232429" />
            </StyledTooltip>
          </Td>
          {isLoading ? (
            <Td>
              <Bone />
            </Td>
          ) : (
            <Td rowSpan={3} style={{ textAlign: 'center', position: 'relative' }}>
              <Table style={{ height: '100%' }}>
                <Tbody>
                  <Tr>
                    <Td>
                      <var>5</var>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <var>1</var>
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>
                      <var>4</var>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
              <OverlayDiv>
                {hasAnalytics ? (
                  <GenerateButton type="button" onClick={showForecast}>
                    Generate demand forecast
                  </GenerateButton>
                ) : (
                  <div>
                    <IconLockClosed />
                    {!hasBeenClicked ? (
                      <Text mt={2} style={{ maxWidth: '235px' }}>
                        Unlock the power of your data with Lightspeed Analytics.{' '}
                        <AnalyticsPromoModalLink>Learn more</AnalyticsPromoModalLink>
                      </Text>
                    ) : (
                      <Text mt={2} style={{ maxWidth: '210px' }}>
                        An account manager will be reaching out to you by email soon.
                      </Text>
                    )}
                  </div>
                )}
              </OverlayDiv>
            </Td>
          )}
        </Tr>
        <Tr>
          <Td>
            Suggested level{' '}
            <StyledTooltip content={SuggestedLevelTooltipText}>
              <IconHelp baseColor="none" detailsColor1="#232429" detailsColor2="#232429" />
            </StyledTooltip>
          </Td>
          {isLoading && (
            <Td>
              <Bone />
            </Td>
          )}
        </Tr>
        <Tr>
          <Td>
            Suggested need{' '}
            <StyledTooltip content={SuggestedNeedTooltipText}>
              <IconHelp baseColor="none" detailsColor1="#232429" detailsColor2="#232429" />
            </StyledTooltip>
          </Td>
          {isLoading && (
            <Td>
              <Bone />
            </Td>
          )}
        </Tr>
      </Tbody>
    </Table>
  );
};
